// src/queryMonitor.js
// Times every Firestore read the app makes — getDoc, getDocs and onSnapshot
// are imported from here instead of "firebase/firestore" (same signatures).
// Each read is recorded with the page it ran on, how long its first result
// took, how many documents came back and whether they came from the server or
// the local cache; Billing → Performance lists them for this browser tab.
// The same timings also go to Firebase Performance Monitoring as custom traces
// (one per kind of read, values left out), so they can be compared across
// everyone's devices in the Firebase console.
import {
  getDoc as fsGetDoc,
  getDocs as fsGetDocs,
  onSnapshot as fsOnSnapshot,
} from "firebase/firestore";
import { trace } from "firebase/performance";
import { perf } from "./firebase";

const MAX_RECORDS = 500;
const records = [];
const listeners = new Set();
let nextId = 1;

function notify() {
  for (const fn of listeners) fn();
}

export function subscribeReads(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function getReads() {
  return records;
}
export function clearReads() {
  records.length = 0;
  notify();
}

// "billingEntries where status == draft" — and a version without the values
// for the Performance Monitoring trace name, so every account's statement
// counts as the same kind of read.
function describe(target) {
  try {
    if (target.type === "document") return { label: target.path, kind: target.path.split("/").slice(0, -1).join("/") + "/{id}" };
    if (target.type === "collection") return { label: target.path, kind: target.path };
    const q = target._query; // a Query: not public API, so fall back quietly
    const path = q.path.canonicalString() || q.collectionGroup || "query";
    const filters = (q.filters || []).flatMap((f) => (f.filters ? f.filters : [f]));
    const parts = filters.map((f) => ({ field: f.field?.canonicalString?.() || "?", op: f.op || "?", value: f.value }));
    const show = (v) => {
      const raw = v?.stringValue ?? v?.integerValue ?? v?.doubleValue ?? v?.booleanValue ?? (v?.timestampValue ? "date" : "…");
      return String(raw).slice(0, 40);
    };
    const limit = q.limit != null ? ` limit ${q.limit}` : "";
    return {
      label: `${path}${parts.length ? " where " + parts.map((p) => `${p.field} ${p.op} ${show(p.value)}`).join(", ") : ""}${limit}`,
      kind: `${path}${parts.length ? " by " + parts.map((p) => p.field).join(",") : ""}`,
    };
  } catch {
    return { label: "query", kind: "query" };
  }
}

function start(method, target) {
  const { label, kind } = describe(target);
  const rec = {
    id: nextId++,
    method,
    label,
    kind,
    page: window.location.pathname,
    startedAt: Date.now(),
    t0: performance.now(),
    ms: null,
    docs: null,
    fromCache: null,
    updates: 0,
    error: null,
  };
  records.unshift(rec);
  if (records.length > MAX_RECORDS) records.length = MAX_RECORDS;
  return rec;
}

function sendTrace(rec) {
  if (!perf) return;
  try {
    // Trace names: at most 100 characters, no leading underscore.
    const t = trace(perf, `${rec.method} ${rec.kind}`.replace(/^_+/, "").slice(0, 100));
    t.record(rec.startedAt, Math.max(1, Math.round(rec.ms)), {
      metrics: { docs: rec.docs || 0 },
      attributes: {
        page: rec.page.replace(/\/[A-Za-z0-9_-]{15,}/g, "/:id").slice(0, 100),
        source: rec.fromCache ? "cache" : "server",
      },
    });
  } catch {
    // monitoring must never break a read
  }
}

function finish(rec, snap) {
  rec.ms = performance.now() - rec.t0;
  rec.docs = snap?.docs ? snap.docs.length : snap?.exists ? (snap.exists() ? 1 : 0) : null;
  rec.fromCache = !!snap?.metadata?.fromCache;
  sendTrace(rec);
  notify();
}

function fail(rec, err) {
  rec.ms = performance.now() - rec.t0;
  rec.error = err?.code || err?.message || String(err);
  notify();
}

export async function getDoc(ref) {
  const rec = start("getDoc", ref);
  try {
    const snap = await fsGetDoc(ref);
    finish(rec, snap);
    return snap;
  } catch (err) {
    fail(rec, err);
    throw err;
  }
}

export async function getDocs(q) {
  const rec = start("getDocs", q);
  try {
    const snap = await fsGetDocs(q);
    finish(rec, snap);
    return snap;
  } catch (err) {
    fail(rec, err);
    throw err;
  }
}

// Times the first result (what the page waits for) and counts the updates
// after it. Accepts every onSnapshot form the SDK does.
export function onSnapshot(target, ...args) {
  const rec = start("listen", target);
  const optsFirst = args[0] && typeof args[0] === "object" && !("next" in args[0]) && typeof args[0] !== "function";
  const options = optsFirst ? args[0] : null;
  const rest = optsFirst ? args.slice(1) : args;
  let next, error, complete;
  if (rest[0] && typeof rest[0] === "object") ({ next, error, complete } = rest[0]);
  else [next, error, complete] = rest;

  let first = true;
  const wrappedNext = (snap) => {
    if (first) {
      first = false;
      finish(rec, snap);
    } else {
      rec.updates++;
      notify();
    }
    if (next) next(snap);
  };
  const wrappedError = (err) => {
    fail(rec, err);
    if (error) error(err);
    else console.error("Firestore listener failed:", rec.label, err);
  };
  return options
    ? fsOnSnapshot(target, options, wrappedNext, wrappedError, complete)
    : fsOnSnapshot(target, wrappedNext, wrappedError, complete);
}
